import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SkeletonModule } from 'primeng/skeleton';
import { DrawerComponent } from '@/shared/ui-components/drawer/drawer.component';
import { InputComponent } from '@/shared/ui-components/input/input.component';
import { ButtonComponent } from '@/shared/ui-components/button/button.component';
import { TagComponent } from '@/shared/ui-components/tag/tag.component';
import { DocPreviewDialogComponent } from '@/shared/ui-components/doc-preview-dialog/doc-preview-dialog.component';
import { AiChatService } from '../../ai-chat.service';
import { AI_CHAT_DRAWER_ID } from './ai-chat-drawer.service';
import { formatAssistantMarkdown } from '../../utilities/format-chat-markdown.util';
import { AiGuideDocument } from '../../data-access/ai-chat.model';

@Component({
    selector: 'app-ai-chat-drawer',
    standalone: true,
    imports: [CommonModule, SkeletonModule, DrawerComponent, InputComponent, ButtonComponent, TagComponent, DocPreviewDialogComponent],
    templateUrl: './ai-chat-drawer.component.html'
})
export class AiChatDrawerComponent {
    public readonly drawerId = AI_CHAT_DRAWER_ID;
    public readonly draft = signal<string>('');

    public readonly previewVisible = signal(false);
    public previewUrl: string | null = null;
    public previewFileName: string | null = null;

    public readonly chatService = inject(AiChatService);

    /** For highlighting the active thread in the "Recent" list. */
    public readonly activeConversationId = computed(() => {
        const id = this.chatService.conversationId();
        return id ? Number(id) : null;
    });

    /** Only ever applied to the assistant's own text — the user's own message stays plain interpolation. */
    protected readonly formatAssistantMarkdown = formatAssistantMarkdown;

    public isFromUser(role: string): boolean {
        return role === 'user';
    }

    public onSend(): void {
        const text = this.draft();
        if (!text.trim() || this.chatService.isStreaming()) {
            return;
        }
        this.draft.set('');
        void this.chatService.sendMessage(text);
    }

    public onSuggestedQuestionClick(question: string): void {
        if (this.chatService.isStreaming()) {
            return;
        }
        void this.chatService.sendMessage(question);
    }

    public onNewConversation(): void {
        this.chatService.resetConversation();
    }

    public onOpenConversation(id: number): void {
        this.chatService.openConversation(id);
    }

    public openGuide(guide: AiGuideDocument): void {
        const url = guide.downloadUrl?.trim();
        if (!url) {
            return;
        }
        this.previewUrl = url;
        this.previewFileName = guide.originalFileName || guide.title;
        this.previewVisible.set(true);
    }

    public formatFileSize(sizeBytes: number): string {
        if (!sizeBytes || sizeBytes <= 0) {
            return '';
        }
        const units = ['B', 'KB', 'MB', 'GB'];
        let value = sizeBytes;
        let unitIndex = 0;
        while (value >= 1024 && unitIndex < units.length - 1) {
            value /= 1024;
            unitIndex += 1;
        }
        return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
    }
}
